import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea.jsx';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, AlertTriangle, Mail, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralReview = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState({});
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/referrals/review`);
      setItems(res.data?.items || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Prüffälle');
    } finally {
      setLoading(false);
    }
  };

  const decide = async (referralId, decision) => {
    const reason = (reasons[referralId] || '').trim();
    if (!reason) {
      toast.error('Bitte eine Begründung angeben');
      return;
    }
    setProcessingId(referralId);
    try {
      await axios.post(`${API}/superadmin/referrals/${referralId}/decide`, { decision, reason });
      toast.success(decision === 'approve' ? '40 € Rabatt-Anspruch freigeschaltet' : 'Empfehlung abgelehnt');
      setItems((prev) => prev.filter((i) => i.referral_id !== referralId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Entscheidung fehlgeschlagen');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="referral-review-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Empfehlungs-Prüffälle
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Unklare Dubletten im „Freunde werben Freunde“-Programm. Begründete Entscheidung schaltet den
            40 € Rabatt (inkl. USt) frei (Freigabe) oder lehnt ihn ab.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <Card data-testid="referral-review-empty">
            <CardContent className="py-12 text-center space-y-3">
              <div className="flex justify-center">
                <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold">Keine offenen Prüffälle</h3>
              <p className="text-sm text-muted-foreground">Aktuell gibt es keine unklaren Empfehlungen zu entscheiden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Card key={item.referral_id} data-testid={`referral-review-item-${item.referral_id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      Prüffall
                    </CardTitle>
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      review_required
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {item.review_reason || 'Unklare Identität/Dublette'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Werber</p>
                      <p className="font-medium flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{item.referrer?.company_name || '—'}</p>
                      <p className="text-muted-foreground flex items-center gap-1 mt-0.5"><Mail className="h-3.5 w-3.5" />{item.referrer?.contact_email || '—'}</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Geworbener Neukunde</p>
                      <p className="font-medium flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{item.referred?.company_name || '—'}</p>
                      <p className="text-muted-foreground flex items-center gap-1 mt-0.5"><Mail className="h-3.5 w-3.5" />{item.referred?.contact_email || '—'}</p>
                    </div>
                  </div>
                  <Textarea
                    data-testid={`referral-review-reason-${item.referral_id}`}
                    placeholder="Begründung der Entscheidung (Pflicht)…"
                    value={reasons[item.referral_id] || ''}
                    onChange={(e) => setReasons((p) => ({ ...p, [item.referral_id]: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <Button
                      data-testid={`referral-approve-${item.referral_id}`}
                      onClick={() => decide(item.referral_id, 'approve')}
                      disabled={processingId === item.referral_id}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1"
                    >
                      <CheckCircle className="h-4 w-4" /> Freigeben (40 € Rabatt)
                    </Button>
                    <Button
                      data-testid={`referral-reject-${item.referral_id}`}
                      onClick={() => decide(item.referral_id, 'reject')}
                      disabled={processingId === item.referral_id}
                      variant="destructive"
                      className="gap-1"
                    >
                      <XCircle className="h-4 w-4" /> Ablehnen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReferralReview;
